// ShowOwnCustomCursorPreference — 自分の確定済みカスタムカーソル
// (custom-cursor-2026-09-19.md §2.5-1) を **自分の画面にも** 表示するかの
// per-user 設定を localStorage に永続化する infrastructure 層ユーティリティ.
//
// 背景 (作者 staging フィードバック 2026-09-19 follow-up):
//   - 「自分に出ると絵を描くのに邪魔になる可能性がある」。
//   - 既定は **ON** (作った直後に自分でも見えて嬉しい). OFF にできる端末ローカル
//     設定を用意する.
//   - OFF は **自分の画面での表示だけ**に影響する。相手への配信・相手側の表示・
//     参加者リストのサムネイルは一切変えない (= `member.cursor-changed` の
//     broadcast やサーバ状態は無関係、`BrushCursorOverlay` の描画を止めるだけ).
//
// 設計 (`brushCursorPreference.ts` と同流儀):
//   - bool を文字列 (`'on' | 'off'`) で保存。parse 失敗 / 不在 / storage 不可は
//     **既定 (= ON)** に倒し、例外を throw しない。
//   - StorageLike 抽象 + sentinel probe で Safari private browsing / Quota を弾く。
//   - store factory は test で storage mock を注入可能。
//
// Tests: showOwnCustomCursorPreference.test.ts (storage-injection style)。

/** localStorage キー. */
export const SHOW_OWN_CUSTOM_CURSOR_KEY = 'oekaki:show-own-custom-cursor';

/** 既定値 (= ON、作った直後に自分でも見えて嬉しい). */
export const DEFAULT_SHOW_OWN_CUSTOM_CURSOR = true;

/**
 * Minimal Storage shape this module uses (brushCursorPreference.ts と同流儀).
 * `globalThis.localStorage` matches; tests pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ShowOwnCustomCursorPreferenceStore {
  /** 自分のカーソルを自分の画面にも表示するか. 不在 / 不正 / storage 不可 → 既定 (ON). */
  get(): boolean;
  /** 設定を保存. storage 不可なら no-op (例外を throw しない). */
  set(show: boolean): void;
  /** key を削除 (= 既定に戻す). テスト / 設定リセット用. storage 不可なら no-op. */
  clear(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`.
 * brushCursorPreference.ts の probe を流用 (sentinel write + readback で Safari
 * private browsing / QuotaExceeded を弾く).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:show-own-custom-cursor__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateShowOwnCustomCursorPreferenceStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= getter always returns the default, setter silently drops).
   *
   * テストでは小さな `Map` ベースの fake を渡す.
   */
  storage?: StorageLike | null;
}

const readShowOwnCustomCursor = (storage: StorageLike): boolean => {
  try {
    const raw = storage.getItem(SHOW_OWN_CUSTOM_CURSOR_KEY);
    if (raw === 'on') return true;
    if (raw === 'off') return false;
    // 不在 or 不正値 → 既定 (ON).
    return DEFAULT_SHOW_OWN_CUSTOM_CURSOR;
  } catch {
    return DEFAULT_SHOW_OWN_CUSTOM_CURSOR;
  }
};

const writeShowOwnCustomCursor = (storage: StorageLike, show: boolean): void => {
  try {
    storage.setItem(SHOW_OWN_CUSTOM_CURSOR_KEY, show ? 'on' : 'off');
  } catch {
    // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる.
  }
};

/**
 * `ShowOwnCustomCursorPreferenceStore` のインスタンスを構築. storage が
 * unavailable な環境 (SSR / Safari private browsing 厳格モード) でも getter は
 * 既定 (ON) を返すだけで、setter は no-op (= 例外で UI が壊れることはない).
 */
export const createShowOwnCustomCursorPreferenceStore = (
  opts: CreateShowOwnCustomCursorPreferenceStoreOptions = {},
): ShowOwnCustomCursorPreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    get() {
      if (storage === null) return DEFAULT_SHOW_OWN_CUSTOM_CURSOR;
      return readShowOwnCustomCursor(storage);
    },
    set(show) {
      if (storage === null) return;
      writeShowOwnCustomCursor(storage, show);
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(SHOW_OWN_CUSTOM_CURSOR_KEY);
      } catch {
        // ignore
      }
    },
  };
};
